import React from 'react';
import { Calculator, Wallpaper } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  wallpaperTypes
}) => {
  // Функция расчета
  const calculateWallpaper = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Стандартные размеры комнат в российских квартирах
  const standardRooms = [
    { name: 'Кухня', length: 3, width: 2.5, height: 2.7 },
    { name: 'Гостиная', length: 5, width: 4, height: 2.7 },
    { name: 'Спальня', length: 4, width: 3, height: 2.7 },
    { name: 'Ванная', length: 1.7, width: 1.5, height: 2.5 }
  ];
  
  // Популярные типы обоев
  const popularWallpaperTypes = ['standard', 'vinyl', 'nonwoven', 'wide'];
  
  // Выбор стандартного размера комнаты
  const selectRoom = (room) => {
    setFormData(prev => ({
      ...prev,
      roomLength: room.length.toString(),
      roomWidth: room.width.toString(),
      roomHeight: room.height.toString(),
      roomPerimeter: (2 * (room.length + room.width)).toFixed(2),
      roomArea: (2 * (room.length + room.width) * room.height).toFixed(2)
    }));
  };
  
  // Выбор типа обоев
  const selectWallpaperType = (typeId) => {
    const selectedType = wallpaperTypes.find(type => type.id === typeId);
    if (selectedType) {
      setFormData(prev => ({
        ...prev,
        wallpaperType: typeId,
        rollWidth: selectedType.defaultWidth.toString(),
        glueType: selectedType.recommendedGlue
      }));
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Выбор стандартной комнаты */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Типовые комнаты:
          </div>
          <div className="grid grid-cols-2 gap-2">
            {standardRooms.map(room => (
              <button
                key={room.name}
                type="button"
                onClick={() => selectRoom(room)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 
                  dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600`}
              >
                <div className="font-medium">{room.name}</div>
                <div className="text-xs opacity-75">{room.length}×{room.width} м</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа обоев */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Тип обоев:
          </div>
          <div className="grid grid-cols-2 gap-2">
            {popularWallpaperTypes.map(typeId => {
              const type = wallpaperTypes.find(t => t.id === typeId);
              if (!type) return null;
              
              return (
                <button
                  key={typeId}
                  type="button"
                  onClick={() => selectWallpaperType(typeId)}
                  className={`p-2 text-sm rounded-lg border transition-colors
                    ${formData.wallpaperType === typeId 
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  <div className="font-medium">{type.name}</div>
                  <div className="text-xs opacity-75">{type.defaultWidth} см</div>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Высота потолка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Высота потолка</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.roomHeight} м</span>
          </div>
          <div className="flex items-center justify-between space-x-2">
            {[2.5, 2.7, 3.0, 3.2].map(height => (
              <button
                key={height}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, roomHeight: height.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${parseFloat(formData.roomHeight) === height 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {height} м
              </button>
            ))}
          </div>
        </div>
        
        {/* Запас материала */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Запас материала</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.wastagePercent}%</span>
          </div>
          <div className="flex items-center justify-between space-x-2">
            {[5, 10, 15, 20].map(percent => (
              <button
                key={percent}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, wastagePercent: percent.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${parseInt(formData.wastagePercent) === percent 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Опции проемов */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Проемы:
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                includeWindows: !prev.includeWindows,
                windowsCount: prev.includeWindows ? '0' : '1'
              }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.includeWindows 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Окна</div>
              <div className="text-xs opacity-75">{formData.includeWindows ? 'Учитываются' : 'Не учитываются'}</div>
            </button>
            
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                includeDoors: !prev.includeDoors,
                doorsCount: prev.includeDoors ? '0' : '1'
              }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.includeDoors 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Двери</div>
              <div className="text-xs opacity-75">{formData.includeDoors ? 'Учитываются' : 'Не учитываются'}</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateWallpaper}
          icon={<Wallpaper className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать обои
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;